import React, { useEffect, useRef, useState } from 'react';
import { DeployStepStatus } from '@/types/board';
import {
  StepRow,
  StepNode,
  ConnectorLine,
  StepContent,
  StepLabel,
  ElapsedTime,
  StepMessage,
} from './styles';
import { DeployStep, ANIMATION_TIMING } from './constants';
import DeployStepIcon from './DeployStepIcon';

interface DeployStepItemProps {
  step: DeployStep;
  status: DeployStepStatus;
  message: string;
  timeText: string;
  isFirst: boolean;
  isLast: boolean;
  compactInProgress?: boolean;
}

const DeployStepItem: React.FC<DeployStepItemProps> = ({
  step,
  status,
  message,
  timeText,
  isFirst,
  isLast,
  compactInProgress = false,
}) => {
  // 애니메이션 상태 관리
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false); // pending → in_progress 확장 애니메이션
  const [isWaitingPrev, setIsWaitingPrev] = useState(false); // 전 단계 애니메이션 대기 중
  const [showCompletedMessage, setShowCompletedMessage] = useState(false);
  const [iconScale, setIconScale] = useState(1);
  const prevStatusRef = useRef<DeployStepStatus>(status);

  // 지연 대기 중에는 pending처럼 표시
  const isActive = status === 'in_progress' && !isWaitingPrev;
  const isDone = status === 'completed' || status === 'error';
  const visualStatus = isWaitingPrev ? 'pending' : status;

  // 상태 변경 감지 및 애니메이션 트리거
  useEffect(() => {
    const prevStatus = prevStatusRef.current;

    // in_progress -> completed/error 전환 시 애니메이션
    if (prevStatus === 'in_progress' && isDone) {
      setIsAnimating(true);
      setIsExpanding(false);

      // 1. 스피너 축소 시작 (0ms)
      setIconScale(0);

      // 2. 완료 메시지 표시
      setTimeout(() => {
        setShowCompletedMessage(true);
        setIsAnimating(false);
      }, ANIMATION_TIMING.STEP_COMPLETE);
    }

    if (prevStatus === 'pending' && status === 'error') {
      setShowCompletedMessage(true);
    }

    // pending -> in_progress 전환 시 확장 애니메이션
    if (prevStatus === 'pending' && status === 'in_progress') {
      // 첫 번째 step이 아니면 전 단계 애니메이션 완료 대기
      const delay = isFirst ? 0 : ANIMATION_TIMING.STEP_COMPLETE;

      setIconScale(1);
      setShowCompletedMessage(false);

      if (delay > 0) {
        setIsWaitingPrev(true);
      }

      setTimeout(() => {
        setIsWaitingPrev(false);
        setIsExpanding(true);

        // 확장 애니메이션 완료 후 상태 리셋
        setTimeout(() => {
          setIsExpanding(false);
        }, ANIMATION_TIMING.EXPAND_DURATION);
      }, delay);
    }

    prevStatusRef.current = status;
  }, [status, isDone, isFirst]);

  // 초기 렌더링 시 completed 상태면 메시지 바로 표시
  useEffect(() => {
    if (isDone && !isAnimating) {
      setShowCompletedMessage(true);
    }
  }, []);

  return (
    <StepRow>
      <StepNode isActive={isActive}>
        <ConnectorLine
          sx={{
            ...(isFirst && { top: 58 }),
            ...(isLast && { bottom: 'auto', height: 58 }),
          }}
        />
        <DeployStepIcon
          status={isWaitingPrev ? 'pending' : status}
          isAnimating={isAnimating}
          isExpanding={isExpanding}
          iconScale={iconScale}
          compactInProgress={compactInProgress}
        />
      </StepNode>

      <StepContent isActive={isActive} isDone={isDone}>
        {/* Step 라벨 + 시간/상태 */}
        <StepLabel status={visualStatus}>
          {step.label}
          {timeText && !isWaitingPrev && (
            <ElapsedTime
              isCompleted={status === 'completed'}
              isError={status === 'error'}
            >
              {timeText}
            </ElapsedTime>
          )}
        </StepLabel>

        {/* 완료/에러일 때: 완료 메시지 (애니메이션 적용) */}
        {isDone && (showCompletedMessage || status === 'error') && message && (
          <StepMessage
            sx={{
              opacity: 0,
              transform: 'translateY(8px)',
              animation:
                'fadeSlideUp 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
              '@keyframes fadeSlideUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(8px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {message}
          </StepMessage>
        )}
      </StepContent>
    </StepRow>
  );
};

export default DeployStepItem;
